/**
 * Paper Atlas · Signature Reward Moment
 * ────────────────────────────────────────────────────────────
 * <InkThroughReveal /> — the signature reward animation.
 *
 * Draws an SVG path (usually a route on a map, or a certificate
 * border) with strokeDashoffset over 400 ms, then fades the fill
 * in behind it. Used for level-up, mastery, and certificate
 * reveal. NEVER used for a small win — those get a stamp-in.
 *
 * Respects prefers-reduced-motion (fades only, no path draw).
 */

import React, { useEffect, useRef, useState } from "react";
import { respectsReducedMotion } from "@/design/tokens";

export function InkThroughReveal({
  d,
  width = 320,
  height = 200,
  strokeColor = "#22385A",
  fillColor = "#F4EAD0",
  strokeWidth = 3,
  autoPlay = true,
  onDone,
}) {
  const pathRef = useRef(null);
  const [length, setLength] = useState(0);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    if (pathRef.current) {
      const l = pathRef.current.getTotalLength();
      setLength(l);
    }
  }, [d]);

  useEffect(() => {
    if (!autoPlay || !length) return;
    if (respectsReducedMotion()) {
      setDrawn(true);
      onDone && setTimeout(onDone, 200);
      return;
    }
    // Kick the animation on the next frame.
    requestAnimationFrame(() => setDrawn(true));
    const t = setTimeout(() => { onDone && onDone(); }, 420);
    return () => clearTimeout(t);
  }, [length, autoPlay, onDone]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto"
      aria-hidden
    >
      {/* Fill (fades in after the ink is drawn) */}
      <path
        d={d}
        fill={fillColor}
        opacity={drawn ? 0.4 : 0}
        style={{ transition: "opacity 300ms cubic-bezier(0.16, 1, 0.3, 1) 200ms" }}
      />
      {/* Ink stroke (draws through) */}
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={length}
        strokeDashoffset={drawn ? 0 : length}
        style={{
          transition: respectsReducedMotion()
            ? "none"
            : "stroke-dashoffset 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
    </svg>
  );
}
